/**
 * Credits Service - 数据服务
 * 负责获取和管理 Credits 相关数据
 */

import type { SwitchTenantParams, SwitchTenantResponse } from '../apis/credits-api';
import type {
    CreditsSummary,
    UsageRecord,
    CreditChangeRecord,
    EntitlementContext,
    WalletStatus,
    UpgradeConfirmInfo,
    DowngradeConfirmInfo,
    GetUsageRecordsParams,
    GetCreditChangesParams,
    TierLevel,
    RechargeRecordsResponse,
    ConsumeRecordsParams,
    ConsumeRecordsResponse,
    PersonalCreditsDetailResponse,
    CompanyCreditsDetailResponse,
    RechargeEligibilityInfo,
    ActivityRechargeParams,
    ActivityRechargeResponse,
} from '../types';

export const CreditsService = Symbol('CreditsService');

export interface CreditsService {
    /**
     * 获取 Credits Summary
     */
    getCreditsSummary(): Promise<CreditsSummary>;

    /**
     * 获取 Usage Records
     */
    getUsageRecords(params?: GetUsageRecordsParams): Promise<{
        records: UsageRecord[];
        total: number;
        page: number;
        pageSize: number;
    }>;

    /**
     * 获取 Credit Changes
     */
    getCreditChanges(params?: GetCreditChangesParams): Promise<{
        records: CreditChangeRecord[];
        total: number;
        page: number;
        pageSize: number;
    }>;

    /**
     * 获取充值记录（个人用户）
     */
    getRechargeRecords(): Promise<RechargeRecordsResponse>;

    /**
     * 获取消费记录（游标分页）
     */
    getConsumeRecords(params?: ConsumeRecordsParams): Promise<ConsumeRecordsResponse>;

    /**
     * 获取个人积分详情
     */
    getPersonalCreditsDetail(tenantUserId: string): Promise<PersonalCreditsDetailResponse>;

    /**
     * 获取公司积分详情
     */
    getCompanyCreditsDetail(tenantUserId: string, companyId: string): Promise<CompanyCreditsDetailResponse>;

    /**
     * 获取权益上下文
     */
    getEntitlementContext(): Promise<EntitlementContext>;

    /**
     * 获取钱包状态
     */
    getWalletStatus(): Promise<WalletStatus>;

    /**
     * 获取升级确认信息
     */
    getUpgradeConfirmInfo(targetTier: TierLevel): Promise<UpgradeConfirmInfo>;

    /**
     * 执行升级
     */
    executeUpgrade(targetTier: TierLevel): Promise<{ success: boolean; checkoutUrl?: string; error?: string }>;

    /**
     * 获取降级确认信息
     */
    getDowngradeConfirmInfo(targetTier: TierLevel): Promise<DowngradeConfirmInfo>;

    /**
     * 执行降级预约
     */
    executeDowngrade(targetTier: TierLevel): Promise<{ success: boolean; scheduledEffectiveAt?: string; error?: string }>;

    /**
     * 取消降级预约
     */
    cancelDowngrade(): Promise<{ success: boolean; error?: string }>;

    /**
     * 领取免费 Credits
     */
    claimFreeCredits(): Promise<{ success: boolean; creditsGranted?: number; expiresAt?: string; error?: string }>;

    /**
     * 检查是否可以领取免费 Credits
     */
    checkFreeCreditsEligibility(): Promise<{ eligible: boolean; nextClaimDate?: string }>;

    /**
     * 检查积分充值资格
     */
    getRechargeEligibility(): Promise<RechargeEligibilityInfo>;

    /**
     * 活动积分充值
     */
    rechargeActivityCredits(params: ActivityRechargeParams): Promise<ActivityRechargeResponse>;
    /**
     * 切换租户
     */
    switchTenant(data: SwitchTenantParams | string): Promise<SwitchTenantResponse>;
}
